import { type ColumnDef } from "@tanstack/react-table"
import type {
  IFulfillmentBatch,
  IPendingRewardDetail,
  IBasePendingRewardsSummary,
  IAllRewards,
  IVendorReward,
} from "@apis/rewards/types"
import {
  selectColumn,
  CurrencyCell,
  SortingHeader,
} from "@utils/table/columnFactories"
import { SquareArrowOutUpRight } from "lucide-react"
import { Link } from "react-router"

const createSharedTableColumns = <
  T extends IBasePendingRewardsSummary,
>(): ColumnDef<T>[] => [
  {
    accessorKey: "rewards_pending_approved_amount_count",
    header: (props) => <SortingHeader {...props} label="Pending Approval" />,
  },
  {
    accessorKey: "rewards_pending_approved_amount_avg",
    header: "Avg",
    cell: (props) => <CurrencyCell {...props} />,
  },
  {
    accessorKey: "rewards_pending_approved_amount_sum",
    header: "Sum",
    cell: (props) => <CurrencyCell {...props} />,
  },
  {
    accessorKey: "rewards_pending_fulfillment_amount_count",
    header: (props) => <SortingHeader {...props} label="Pending Fulfillment" />,
  },
  {
    accessorKey: "rewards_pending_fulfillment_amount_avg",
    header: "Avg",
    cell: (props) => <CurrencyCell {...props} />,
  },
  {
    accessorKey: "rewards_pending_fulfillment_amount_sum",
    header: "Sum",
    cell: (props) => <CurrencyCell {...props} />,
  },
]

export const tableColumnsSummary: ColumnDef<IAllRewards>[] = [
  {
    accessorKey: "fulfillment_vendor_code",
    header: "Fulfillment Vendor Code",
  },
  ...createSharedTableColumns<IAllRewards>(),
]

export const tableColumnsVendorSummary: ColumnDef<IVendorReward>[] = [
  {
    accessorKey: "fulfillment_channel",
    header: "Fulfillment Channel",
  },
  ...createSharedTableColumns<IVendorReward>(),
]

export const tableColumnsFulfillmentBatches: ColumnDef<IFulfillmentBatch>[] = [
  {
    accessorKey: "fulfillment_batch_id",
    header: "Batch",
  },
  {
    accessorKey: "batch_sent_dt",
    header: "Date Sent",
  },
  {
    accessorKey: "fulfillment_vendor",
    header: "Vendor",
  },
  {
    accessorKey: "rewards_sum",
    header: "Rewards",
  },
  {
    accessorKey: "rewards_amount_sum",
    header: "Total",
  },
  {
    accessorKey: "rewards_amount_avg",
    header: "Avg",
  },
  {
    accessorKey: "rewards_pending_fulfillment_sum",
    header: "Pending Fulfillment",
  },
  {
    accessorKey: "rewards_fulfilled_sum",
    header: "Fulfilled",
  },
  {
    accessorKey: "rewards_sent_sum",
    header: "Sent",
  },
]

export const tableColumnsPendingBatchDetail: ColumnDef<IPendingRewardDetail>[] =
  [
    selectColumn(),
    {
      accessorKey: "account_number",
      header: "Account Number",
      cell: ({ row }) => {
        const { membership_profile_id, account_id, account_number } =
          row.original
        const href = `/profiles/${membership_profile_id}/accounts/${account_id}`

        return (
          <Link
            to={href}
            target="blank"
            aria-label="View account details"
            className="tw:flex tw:items-center tw:gap-1 tw:text-black tw:hover:text-blue-600 tw:hover:no-underline"
          >
            <span className="tw:inline-block tw:text-black">
              {account_number}
            </span>
            <SquareArrowOutUpRight
              size={24}
              className="tw:inline-block tw:p-1"
            />
          </Link>
        )
      },
    },
    {
      accessorKey: "redeemed_dt",
      header: "Redeemed Dt",
    },
    {
      accessorKey: "reward_amount",
      header: "Reward Amount",
      cell: (props) => <CurrencyCell {...props} />,
    },
    {
      accessorKey: "reward_description",
      header: "Description",
    },
  ]
